/**
 * Example node schema for the markdown-flow editor.
 *
 * The example node represents a step that provides exact content
 * to be sent (or used as AI guidance based on strict mode).
 *
 * It supports multiple channels (SMS, Email, etc.) with tab-based
 * switching in the UI. Channel availability is determined at the
 * procedure level based on whether required fields are present.
 */
import { ObjectId } from "bson";
import * as z from "zod";
import {
  exampleChannels,
  ExampleChannelsContentSchema,
  type ExampleChannel,
  type ExampleChannelsContent,
} from "../../../procedure-step-types/channel-example-schemas";
import { FlowContentNodeSchema } from "../flow-content-node-schema";
import { InlineLockAttrsSchema } from "../lock-types-schema";

// =============================================================================
// EXAMPLE NODE SCHEMA
// =============================================================================

/**
 * Example node - a step that provides channel-specific example content.
 *
 * Structure:
 * - type: Always "example"
 * - instanceId: Unique identifier for this node instance
 * - stepTypeValue: Always "example" (links to StepTypeConfig.value)
 * - activeChannel: Currently active channel in the UI (for tab state)
 * - channels: Content for each channel
 * - lock: Optional lock for preventing edits
 *
 * Channel availability is determined at the procedure level based on
 * whether required fields are present for each channel.
 */
const ExampleNodeSchema_ = z.object({
  type: z.literal("example"),
  instanceId: z.string(),
  stepTypeValue: z.literal("example"),
  /** Currently active channel being edited in the UI */
  activeChannel: z.enum(exampleChannels),
  /** Channel-specific content (only populated channels have content) */
  channels: ExampleChannelsContentSchema,
  lock: InlineLockAttrsSchema.optional(),
});

/** Base type from schema inference (no index signature). Exported for use with Omit. */
export type ExampleNodeBase = z.infer<typeof ExampleNodeSchema_>;

/** Full type with index signature for FlowContentNode compatibility */
export type ExampleNode = ExampleNodeBase & { [key: string]: unknown };

/** Attrs type for tiptap (Omit done on base type before index signature) */
export type ExampleNodeAttrs = Omit<ExampleNodeBase, "type">;

export const ExampleNodeSchema: z.ZodType<ExampleNode> = ExampleNodeSchema_;

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard for example nodes.
 */
export function isExampleNode(node: unknown): node is ExampleNode {
  return (
    typeof node === "object" &&
    node !== null &&
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    (node as Record<string, unknown>).type === "example"
  );
}

// =============================================================================
// HELPER TYPES
// =============================================================================

/**
 * Default example node attributes for creating new nodes.
 *
 * Initializes with the specified channel active and a single empty example.
 * Other channels can be added/edited via the UI.
 */
export function createDefaultExampleNodeAttrs(
  instanceId: string,
  activeChannel: ExampleChannel,
): ExampleNodeAttrs {
  // Initialize with one empty example for the active channel
  const channels: ExampleChannelsContent = {};
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME

  switch (activeChannel) {
    case "sms":
      channels.sms = {
        examples: [{ id: new ObjectId().toHexString(), body: [] }],
        strict: false,
      };
      break;
    case "email":
      channels.email = { subject: [], body: [], strict: false };
      break;
    default:
      throw new Error(`Channel "${activeChannel}" is not yet supported`);
  }

  return { instanceId, stepTypeValue: "example", activeChannel, channels };
}

// =============================================================================
// EXAMPLE ITEM NODE (for TipTap child nodes)
// =============================================================================

/**
 * Schema for individual example items within the TipTap editor.
 * These are serialized to the SmsExampleItem structure in channels.
 */
const ExampleItemNodeSchema_ = z.object({
  type: z.literal("exampleItem"),
  itemId: z.string(),
  content: z.array(FlowContentNodeSchema),
});

export const ExampleItemNodeSchema: z.ZodType<ExampleItemNode> =
  ExampleItemNodeSchema_;
export interface ExampleItemNode {
  type: "exampleItem";
  itemId: string;
  content: FlowContentNode[];
}

/**
 * Type guard for example item nodes.
 */
export function isExampleItemNode(node: unknown): node is ExampleItemNode {
  return (
    typeof node === "object" &&
    node !== null &&
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    (node as Record<string, unknown>).type === "exampleItem"
  );
}

// Import FlowContentNode type for interface
import type { FlowContentNode } from "../flow-content-node-schema";

// Re-export types for convenience

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  type ExampleChannel,
  type ExampleChannelsContent,
} from "../../../procedure-step-types/channel-example-schemas";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { type InlineLockAttrs } from "../lock-types-schema";
